import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';

const Dialog = styled(StyledDialog)`
  width: 464px !important;
  text-align: left;
  padding: 0;
  width: 1000px;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
`;

const Inner = styled.div`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const Teams = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const Team = styled(Button)<{ $active: boolean }>`
  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.purple};
      &:hover {
        border-color: ${theme.colors.purple};
      }
    `}
`;

const Footer = styled.div`
  padding: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

interface Props {
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  links: number[];
}

const CsvExportModal: FC<Props> = ({ links, onClose, onSuccess }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [team, setTeam] = useState<null | number>(null);
  const queryClient = useQueryClient();
  const toasts = useToasts();

  const onExport = async () => {
    const toastId = toasts.create('loading', 'Exporting...');
    setIsExporting(true);

    try {
      await api.post('/v1/eytv-upload-links', {
        links,
        team,
      });

      await queryClient.invalidateQueries(['eytv_links']);
      await queryClient.invalidateQueries(['eytv_files']);

      toasts.success(toastId, 'Exported');
      onSuccess();
      onClose();
    } catch (_) {
      toasts.error(toastId, 'Error!');
    } finally {
      setIsExporting(false);
    }
  };

  const teams = useSelector((state: RootState) => state.teams);

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">Export CSV</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Inner>
          <Teams>
            {Object.values(teams).map((t) => (
              <Team
                key={t.id}
                onClick={() => setTeam(t.id)}
                $active={team === t.id}
                small
                color="secondary"
              >
                {t.name}
              </Team>
            ))}
          </Teams>
        </Inner>
        <Footer>
          <Button
            type="button"
            onClick={onExport}
            disabled={isExporting || !team}
            loading={isExporting}
          >
            Export
          </Button>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default CsvExportModal;
